import { ratePriceAdjustmentBundleIdZodSchema } from "@@/shared/acquire/accommodation-engine/rate-price-adjustment-bundle-id";
import {
    ratePriceAdjustmentIdFactory,
    ratePriceAdjustmentIdZodSchema,
} from "@@/shared/acquire/accommodation-engine/rate-price-adjustment-id";
import { weekdaysOnOffZodObject } from "@@/shared/acquire/accommodation-engine/weekdays-on-off";
import { accommodationRateOptionIdZodSchema } from "@@/shared/acquire/accommodation-rate-option-id";
import { dateRangeZodObject } from "@@/shared/date-time/date-range";
import {
    isoAndUnixZodSchema,
    isoTimestampZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { entityStateZodSchema } from "@@/shared/entity-status";
import { productIdZodSchema } from "@@/shared/products/product-id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { requiredTranslatableZodSchema } from "@@/shared/translations/translation";
import { z } from "zod";

const priceAdjustmentPercentageZodObject = z.object({
    type: z.literal("PERCENTAGE"),
    value: z.number(),
});
type PriceAdjustmentPercentage = z.infer<
    typeof priceAdjustmentPercentageZodObject
>;

const priceAdjustmentAmountZodObject = z.object({
    type: z.literal("AMOUNT"),
    value: z.number(),
});
type PriceAdjustmentAmount = z.infer<typeof priceAdjustmentAmountZodObject>;

const priceAdjustmentZodUnion = z.union([
    priceAdjustmentPercentageZodObject,
    priceAdjustmentAmountZodObject,
]);
type PriceAdjustment = z.infer<typeof priceAdjustmentZodUnion>;

const ratePriceAdjustmentZodObject = z.object({
    _id: ratePriceAdjustmentIdZodSchema,
    _type: z.literal("RATE_PRICE_ADJUSTMENT"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    title: requiredTranslatableZodSchema,
    range: dateRangeZodObject,
    providerId: providerIdZodSchema,
    productId: productIdZodSchema,
    target: accommodationRateOptionIdZodSchema,
    adjustment: priceAdjustmentZodUnion,
    /** If an adjustment has been split into multiple adjustments, they will all share the same bundle id */
    bundleId: ratePriceAdjustmentBundleIdZodSchema.optional(),
    weekdaysOn: weekdaysOnOffZodObject,
    /**
     * List of dates when the adjustment is deactivated. \
     * dates in ISO 8601 format. \
     * start of day in "Europe/Stockholm" timezone. \
     * (later when we've got it, the dates should probably be in the same timezone as the provider)
     */
    inactiveDates: z.array(isoTimestampZodSchema).optional().default([]),
});
type RatePriceAdjustment = z.infer<typeof ratePriceAdjustmentZodObject>;

const ratePriceAdjustmentFactoryParamsZodObject =
    ratePriceAdjustmentZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
        _status: true,
    });
type FactoryParams = z.infer<typeof ratePriceAdjustmentFactoryParamsZodObject>;

const ratePriceAdjustmentFactory = (
    params: FactoryParams,
): RatePriceAdjustment => {
    const command: RatePriceAdjustment = {
        ...params,
        _id: ratePriceAdjustmentIdFactory(),
        _type: "RATE_PRICE_ADJUSTMENT",
        _createdAt: nowIsoAndUnix(),
        _status: "ACTIVE",
    };
    return command;
};
export {
    ratePriceAdjustmentFactory,
    ratePriceAdjustmentFactoryParamsZodObject,
    ratePriceAdjustmentZodObject,
};
export type {
    PriceAdjustment,
    PriceAdjustmentAmount,
    PriceAdjustmentPercentage,
    RatePriceAdjustment,
};
